/**
 * SNMP Metric Profile API Service
 *
 * Provides CRUD operations for SNMP metric profiles, metrics, and walks.
 */

import api from "./api";

const sp = (schemaName) => (schemaName ? { client: schemaName } : {});

// =============================================================================
// SNMPMetricProfile CRUD
// =============================================================================

/**
 * Get all SNMP metric profiles
 * @returns {Promise<Array>} List of SNMPMetricProfile objects
 */
export const getAllSNMPProfiles = async (schemaName = null) => {
  try {
    const response = await api.get("/snmp-profiles/", { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP profiles:", error);
    throw error;
  }
};

/**
 * Get all SNMP metric profiles with pagination
 * @param {Object} params - Query parameters (page, search, ordering, etc.)
 * @returns {Promise<Object>} Paginated response
 */
export const getSNMPProfilesWithPagination = async (params = {}, schemaName = null) => {
  try {
    const response = await api.get("/snmp-profiles/", { params: { ...params, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP profiles:", error);
    throw error;
  }
};

/**
 * Get a single SNMP metric profile by ID
 * @param {number} id - SNMPMetricProfile ID
 * @returns {Promise<Object>} SNMPMetricProfile object with nested metrics and walks
 */
export const getSNMPProfileById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/snmp-profiles/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP profile:", error);
    throw error;
  }
};

/**
 * Create a new SNMP metric profile
 * @param {Object} data - SNMPMetricProfile data (name, description, org)
 * @returns {Promise<Object>} Created SNMPMetricProfile object
 */
export const createSNMPProfile = async (data, schemaName = null) => {
  try {
    const response = await api.post("/snmp-profiles/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating SNMP profile:", error);
    throw error;
  }
};

/**
 * Update an existing SNMP metric profile
 * @param {number} id - SNMPMetricProfile ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated SNMPMetricProfile object
 */
export const updateSNMPProfile = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/snmp-profiles/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating SNMP profile:", error);
    throw error;
  }
};

/**
 * Delete an SNMP metric profile
 * @param {number} id - SNMPMetricProfile ID
 * @returns {Promise<Object>} Response message
 */
export const deleteSNMPProfile = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/snmp-profiles/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting SNMP profile:", error);
    throw error;
  }
};

/**
 * Generate SNMP profiles pillar file
 * @returns {Promise<Object>} Response with file path and profiles count
 */
export const generateSNMPProfilesPillar = async (schemaName = null) => {
  try {
    const response = await api.post("/snmp-profiles/generate_pillar/", {}, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error generating SNMP profiles pillar:", error);
    throw error;
  }
};

/**
 * Import SNMP profiles from a YAML file
 * @param {File} file - The YAML file to import
 * @returns {Promise<Object>} Response with created/updated counts
 */
export const importSNMPProfilesFromYAML = async (file, schemaName = null) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/snmp-profiles/import_yaml/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: sp(schemaName),
    });
    return response.data;
  } catch (error) {
    console.error("Error importing SNMP profiles from YAML:", error);
    throw error;
  }
};

// =============================================================================
// SNMPMetric CRUD
// =============================================================================

/**
 * Get all metrics for an SNMP profile
 * @param {number} profileId - Parent SNMPMetricProfile ID
 * @returns {Promise<Array>} List of SNMPMetric objects
 */
export const getSNMPMetricsByProfile = async (profileId, schemaName = null) => {
  try {
    const response = await api.get(`/snmp-metrics/`, { params: { profile: profileId, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP metrics:", error);
    throw error;
  }
};

/**
 * Get a single SNMP metric by ID
 * @param {number} id - SNMPMetric ID
 * @returns {Promise<Object>} SNMPMetric object
 */
export const getSNMPMetricById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/snmp-metrics/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP metric:", error);
    throw error;
  }
};

/**
 * Create a new SNMP metric
 * @param {Object} data - SNMPMetric data
 * @returns {Promise<Object>} Created SNMPMetric object
 */
export const createSNMPMetric = async (data, schemaName = null) => {
  try {
    const response = await api.post("/snmp-metrics/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating SNMP metric:", error);
    throw error;
  }
};

/**
 * Update an existing SNMP metric
 * @param {number} id - SNMPMetric ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated SNMPMetric object
 */
export const updateSNMPMetric = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/snmp-metrics/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating SNMP metric:", error);
    throw error;
  }
};

/**
 * Delete an SNMP metric
 * @param {number} id - SNMPMetric ID
 * @returns {Promise<Object>} Response message
 */
export const deleteSNMPMetric = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/snmp-metrics/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting SNMP metric:", error);
    throw error;
  }
};

// =============================================================================
// SNMPWalk CRUD
// =============================================================================

/**
 * Get all walks for an SNMP profile
 * @param {number} profileId - Parent SNMPMetricProfile ID
 * @returns {Promise<Array>} List of SNMPWalk objects
 */
export const getSNMPWalksByProfile = async (profileId, schemaName = null) => {
  try {
    const response = await api.get(`/snmp-walks/`, { params: { profile: profileId, ...sp(schemaName) } });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP walks:", error);
    throw error;
  }
};

/**
 * Get a single SNMP walk by ID
 * @param {number} id - SNMPWalk ID
 * @returns {Promise<Object>} SNMPWalk object
 */
export const getSNMPWalkById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/snmp-walks/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching SNMP walk:", error);
    throw error;
  }
};

/**
 * Create a new SNMP walk
 * @param {Object} data - SNMPWalk data
 * @returns {Promise<Object>} Created SNMPWalk object
 */
export const createSNMPWalk = async (data, schemaName = null) => {
  try {
    const response = await api.post("/snmp-walks/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating SNMP walk:", error);
    throw error;
  }
};

/**
 * Update an existing SNMP walk
 * @param {number} id - SNMPWalk ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated SNMPWalk object
 */
export const updateSNMPWalk = async (id, data, schemaName = null) => {
  try {
    const response = await api.put(`/snmp-walks/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating SNMP walk:", error);
    throw error;
  }
};

/**
 * Delete an SNMP walk
 * @param {number} id - SNMPWalk ID
 * @returns {Promise<Object>} Response message
 */
export const deleteSNMPWalk = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/snmp-walks/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting SNMP walk:", error);
    throw error;
  }
};

// =============================================================================
// Default Export
// =============================================================================

export default {
  // SNMPMetricProfile
  getAllSNMPProfiles,
  getSNMPProfilesWithPagination,
  getSNMPProfileById,
  createSNMPProfile,
  updateSNMPProfile,
  deleteSNMPProfile,
  generateSNMPProfilesPillar,
  importSNMPProfilesFromYAML,
  // SNMPMetric
  getSNMPMetricsByProfile,
  getSNMPMetricById,
  createSNMPMetric,
  updateSNMPMetric,
  deleteSNMPMetric,
  // SNMPWalk
  getSNMPWalksByProfile,
  getSNMPWalkById,
  createSNMPWalk,
  updateSNMPWalk,
  deleteSNMPWalk,
};
